import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import OutlinedInput from '@mui/material/OutlinedInput';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import { useNotify } from 'src/context/ContextNotify';
import MedicalDeviceForm from './MedicalDeviceForm';
import { postAPI } from 'src/utils/api';
import { formatDateForInput, getToday, isEmpty } from 'src/common/helper';
import { getToken } from 'src/utils/auth';

const inputStyle = {
  fontSize: '14px',
  my: '8px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#BDBDBD',
  },
  '& .MuiInputBase-input': { padding: '8px 14px' },
  '& input::placeholder': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#CCD1D9',
    opacity: 1,
  },
  backgroundColor: 'primary.light',
  width: '100%',
};

const dateStyle = {
  width: '100%',
  height: '32px',
  my: '8px',
  '& .MuiInputBase-input.MuiOutlinedInput-input': {
    py: '8px',
    fontSize: '14px',
  },
};

const defaultError = {
  patient_name: true,
  patient_number: true,
  creation_date: true,
  expiry_date: true,
  storage_date: true,
  total_quantity: true,
};

type DataType = {
  patient_name: string;
  patient_number: string;
  creation_date: Dayjs | null;
  expiry_date: Dayjs | null;
  storage_date: Dayjs | null;
  total_quantity: number;
};

const DEFAULT_DATA = {
  patient_name: '',
  patient_number: '',
  creation_date: null,
  expiry_date: null,
  storage_date: null,
  total_quantity: 6,
};

const DialogRegister = (props: any) => {
  const notifyContext = useNotify();
  const [patientInfo, setPatientInfo] = useState<DataType>(DEFAULT_DATA);
  const [errors, setErrors] = useState(defaultError);
  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setPatientInfo((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleDateChange = (newValue: dayjs.Dayjs | null, name: string) => {
    if (name === 'creation_date') {
      const { expiry_date, storage_date } = calculateDates(newValue);
      setPatientInfo((prevState) => ({
        ...prevState,
        creation_date: dayjs(newValue),
        expiry_date: dayjs(expiry_date),
        storage_date: dayjs(storage_date),
      }));
      return;
    }

    setPatientInfo((prevState) => ({
      ...prevState,
      [name]: dayjs(newValue),
    }));
  };

  const calculateDates = (creation_date: any) => {
    if (!creation_date) return { expiry_date: '', storage_date: '' };

    const creation_dateObj = new Date(creation_date);
    try {
      formatDateForInput(creation_dateObj)
    } catch (error) {
      return { expiry_date: '', storage_date: '' };
    }

    const expiry_dateObj = new Date(creation_dateObj);
    expiry_dateObj.setMonth(expiry_dateObj.getMonth() + 6);

    const storage_dateObj = new Date(creation_dateObj);
    storage_dateObj.setFullYear(storage_dateObj.getFullYear() + 1);

    return {
      expiry_date: formatDateForInput(expiry_dateObj),
      storage_date: formatDateForInput(storage_dateObj),
    };
  };

  const formatJapaneseDateToISO = (dateString: any) => {
    const [year, month, day] = dateString.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  const handleCaptured = async (lines: any) => {
    const expectedKeys = [
      '医療機関名',
      '患者ID',
      '製造年月日',
      '使用期限',
      '製造元',
    ];
    const foundKeys: any = [];

    let errorMessage = '';
    for (const line of lines) {
      const [key, value] = line.split(': ');

      if (expectedKeys.includes(key)) {
        foundKeys.push(key);
        switch (key) {
          case '医療機関名':
            break;
          case '患者ID':
            if (value) {
              setPatientInfo((prev) => ({
                ...prev,
                patient_number: value.trim(),
                patient_name: '',
              }));
              if (!value) return;

              // const patient_name = await fetchPatientInfo(value);
              // setPatientInfo(prev => ({
              //   ...prev,
              //   patient_name: patient_name
              // }));
            } else {
              errorMessage += '患者IDは取得できませんでした。';
            }
            break;
          case '製造年月日':
            if (value) {
              const formattedDate = formatJapaneseDateToISO(value.trim());
              setPatientInfo((prev) => {
                const { expiry_date, storage_date } =
                  calculateDates(formattedDate);
                return {
                  ...prev,
                  creation_date: dayjs(formattedDate),
                  expiry_date: dayjs(expiry_date),
                  storage_date: dayjs(storage_date),
                };
              });
            } else {
              errorMessage += '製造年月日は取得できませんでした。';
            }
            break;
          default:
            break;
        }
      }
    }

    if (foundKeys.length !== expectedKeys.length) {
      const missingKeys = expectedKeys.filter(
        (key) => !foundKeys.includes(key)
      );
      notifyContext.setNotifyMessage({
        show: true,
        message: `フォーマットが正しくありません。以下の情報が欠落しています: ${missingKeys.join(
          ', '
        )}`,
        type: 'error',
      });
    }

    if (errorMessage !== '') {
      notifyContext.setNotifyMessage({
        show: true,
        message: errorMessage,
        type: 'error',
      });
    }
  };
  const validate = () => {
    if (isEmpty(patientInfo.patient_number)) {
      setErrors({ ...defaultError, patient_number: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '患者IDを入力してください',
        type: 'error',
      });
      return false;
    }
    if (isEmpty(patientInfo.patient_name)) {
      setErrors({ ...defaultError, patient_name: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '患者名を入力してください',
        type: 'error',
      });
      return false;
    }
    if (!patientInfo.creation_date?.isValid() ) {
      setErrors({ ...defaultError, creation_date: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '製造日を入力してください',
        type: 'error',
      });
      return false;
    }
    if (!patientInfo.expiry_date?.isValid() ) {
      setErrors({ ...defaultError, expiry_date: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '保管期限を入力してください',
        type: 'error',
      });
      return false;
    }
    if (!patientInfo.storage_date?.isValid() ) {
      setErrors({ ...defaultError, storage_date: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '消費期限を入力してください',
        type: 'error',
      });
      return false;
    }
    if (isEmpty(patientInfo.total_quantity)) {
      setErrors({ ...defaultError, total_quantity: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '箱内個数を入力してください',
        type: 'error',
      });
      return false;
    }

    setErrors(defaultError);

    notifyContext.setNotifyMessage({
      show: false,
      message: '',
      type: 'error',
    });

    return true;
  };

  const handleRegister = () => {
    try {
      if (!validate()) {
        return;
      }
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      const data = {
        ...patientInfo,
        creation_date:
          patientInfo.creation_date?.format('YYYY-MM-DD') || getToday('YYYY-MM-DD'),
        expiry_date: patientInfo.expiry_date?.format('YYYY-MM-DD') || getToday('YYYY-MM-DD'),
        storage_date: patientInfo.storage_date?.format('YYYY-MM-DD') || getToday('YYYY-MM-DD'),
        user_id: '1',
      };

      postAPI(`/inventory?token=${token}`, data)
        .then((res: any) => {
          if (res.code === 200) {
            console.log(res);
            notifyContext.setNotifyMessage({
              show: true,
              message: '保存出来ました。',
              type: 'success',
            });
            props.handleConfirm(res.data);
          } else {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg || 'Failed to post data',
              type: 'error',
            });
          }
        })
        .finally(() => {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
    >
      <DialogTitle id='scroll-dialog-title'>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Typography fontSize={'20px'} fontWeight={500}>
            PRP在庫登録
          </Typography>
          <IconButton
            aria-label='toggle password visibility'
            edge='end'
            onClick={props.handleClose}
          >
            <Box
              component={'img'}
              src='/images/ic-close.svg'
              alt='icon eye'
              sx={{ width: '24px', height: '24px' }}
            />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ width: '500px' }}>
        {/* <Grid container spacing={3}>
          <Grid item xs={4} display={'flex'} alignItems={'center'}>
            <BtnGradient
              text='画像から読み取る'
              handleButtonClick={handleReadImage}
              width='152px'
            />
          </Grid>
          <Grid item xs>
            <TextField
              id='outlined-multiline-static'
              label=''
              multiline
              rows={3}
              defaultValue={''}
              sx={{ width: '100%' }}
              InputProps={{
                sx: { fontSize: '14px' },
              }}
              onChange={(event) => handleInputChange(event)}
            />
          </Grid>
        </Grid> */}
        <MedicalDeviceForm onCaptured={handleCaptured} />
        <Grid container spacing={3}>
          <Grid item xs={4} display={'flex'} alignItems={'center'}>
            <Typography fontWeight={700}>患者ID</Typography>
          </Grid>
          <Grid item xs>
            <OutlinedInput
              placeholder={'入力してください'}
              sx={inputStyle}
              inputProps={{
                sx: {
                  backgroundColor: 'primary.light',
                },
              }}
              name='patient_number'
              value={patientInfo.patient_number}
              onChange={(event) => handleInputChange(event)}
              error={!errors.patient_number}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={4} display={'flex'} alignItems={'center'}>
            <Typography fontWeight={700}>患者名</Typography>
          </Grid>
          <Grid item xs>
            <OutlinedInput
              placeholder={'入力してください'}
              sx={inputStyle}
              inputProps={{
                sx: {
                  backgroundColor: 'primary.light',
                },
              }}
              name='patient_name'
              value={patientInfo.patient_name}
              onChange={(event) => handleInputChange(event)}
              error={!errors.patient_name}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={4} display={'flex'} alignItems={'center'}>
            <Typography fontWeight={700}>製造日</Typography>
          </Grid>
          <Grid item xs>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name=''
                // value={dateTemp}
                value={patientInfo.creation_date}
                onChange={(newValue) =>
                  handleDateChange(newValue, 'creation_date')
                }
                format='YYYY/MM/DD'
                sx={dateStyle}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={4} display={'flex'} alignItems={'center'}>
            <Typography fontWeight={700}>保管期限</Typography>
          </Grid>
          <Grid item xs>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name=''
                // value={dateTemp}
                value={patientInfo.expiry_date}
                onChange={(newValue) =>
                  handleDateChange(newValue, 'expiry_date')
                }
                format='YYYY/MM/DD'
                sx={dateStyle}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={4} display={'flex'} alignItems={'center'}>
            <Typography fontWeight={700}>消費期限</Typography>
          </Grid>
          <Grid item xs>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name=''
                // value={dateTemp}
                value={patientInfo.storage_date}
                onChange={(newValue) =>
                  handleDateChange(newValue, 'storage_date')
                }
                format='YYYY/MM/DD'
                sx={dateStyle}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={4} display={'flex'} alignItems={'center'}>
            <Typography fontWeight={700}>箱内個数</Typography>
          </Grid>
          <Grid item xs>
            <OutlinedInput
              placeholder={'入力してください'}
              sx={inputStyle}
              inputProps={{
                sx: {
                  backgroundColor: 'primary.light',
                },
              }}
              name='total_quantity'
              value={patientInfo.total_quantity}
              onChange={(event) => handleInputChange(event)}
              error={!errors.total_quantity}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: '20px' }}>
        <BtnOutlineGradient
          text='キャンセル'
          handleButtonClick={props.handleClose}
        />
        <BtnGradient text='登録' handleButtonClick={handleRegister} />
      </DialogActions>
    </Dialog>
  );
};

export default DialogRegister;
